import { useState } from 'react'
import { Box } from '@chakra-ui/react'
import { observer } from 'mobx-react'
import OrgTeamSwitcher from 'components/DashboardBanners/OrgTeamSwitcher'
import OrgContextBanner from 'components/OrgContextBanner'
import SwitchTeamModal from 'components/SwitchTeamModal'
import { useContextSwitcher } from 'hooks/use-context-switcher'

interface IContextBannerProps {
  /**
   * Team-scoped page (Schedule, Assignments): adds the Team to the banner and the
   * Organization/Team switcher underneath it. Organization-level pages omit it —
   * their content belongs to the organization and is shared across its Teams.
   */
  withTeam?: boolean
  /**
   * Drop the component's own page padding, for a host that already provides the
   * gutter — the dashboard's mobile column does. Without it the banner would sit
   * 12px further in than everything stacked above and below it.
   */
  flush?: boolean
}

/**
 * The context banner every list page carries: which organization you are in, and
 * on Team-scoped pages which Team.
 *
 * Pages render `<ContextBanner />` and nothing else — selection, the Team list for
 * the current organization and the switchers live here, so eleven pages cannot
 * drift into eleven slightly different switchers, which is what this ticket exists
 * to undo.
 *
 * Two shapes, because the two levels of context are not the same job. An
 * organization-level page switches one thing, so it carries one action inside the
 * banner. A Team-scoped page switches two related things, so it reuses the
 * dashboard's paired dropdowns below the banner, where picking an organization
 * visibly narrows the Team list beside it.
 *
 * The member count rides along with the org rows this hook already reads, so the
 * banner reports the same number the switch-organization modal does. The team
 * count is left off: no page here has it, and fetching one per page would buy a
 * number nobody came to read.
 */
const ContextBanner = ({ withTeam = false, flush = false }: IContextBannerProps) => {
  const { org, team, teams, organizations, setOrg, setTeam, isLoading } = useContextSwitcher(withTeam)
  const [isOrgOpen, setOrgOpen] = useState(false)

  return (
    <>
      {/* The page frames the banner, matching the Teams page: pages that host it
          sit flush against the sidebar otherwise. `flexBasis` earns its place —
          several of these pages wrap the banner in a reactstrap `Row`, which is a
          flex container, so the banner would otherwise shrink to its own text
          instead of spanning the page like the table beneath it. */}
      <Box px={flush ? 0 : { base: '12px', md: '16px' }} pt={flush ? 0 : '12px'} w="full" flexBasis="100%">
        <OrgContextBanner
          orgName={org?.name ?? ''}
          orgLogo={org?.logoUrl}
          primaryColor={org?.primaryColor}
          sports={(withTeam ? team?.sports : undefined) ?? org?.sports ?? []}
          memberCount={org?.memberCount}
          // Team-scoped pages switch from the cluster below, so the banner carries
          // no action of its own — two places to change the same thing is the
          // confusion this standardisation is undoing.
          onSwitchOrg={withTeam ? undefined : () => setOrgOpen(true)}
          // Absent while showing every Team, so the banner keeps naming the
          // organization rather than claiming a Team that is not in view.
          teamName={withTeam ? team?.name : undefined}
        />

        {withTeam && (
          <Box pt="12px">
            <OrgTeamSwitcher
              organizations={organizations}
              selectedOrgId={org?.id ?? null}
              // Null never arrives for the organization: its "All" row is not
              // rendered, since every request on these pages carries an orgId.
              onOrgChange={orgId => orgId !== null && setOrg(orgId)}
              teams={teams}
              selectedTeamId={team?.id ?? null}
              // Null is meaningful here: it is the "All teams" row, and the page
              // then shows the organization's Teams together.
              onTeamChange={setTeam}
              isLoading={isLoading}
              // "All teams" stays on offer — it is the aggregate view. "All
              // organizations" does not: these pages always query one tenant.
              allowAllOrgs={false}
            />
          </Box>
        )}
      </Box>

      {!withTeam && (
        <SwitchTeamModal
          isOpen={isOrgOpen}
          onClose={() => setOrgOpen(false)}
          mode="dark"
          selectedOrgId={org?.id ?? null}
          onSelect={setOrg}
        />
      )}
    </>
  )
}

export default observer(ContextBanner)
